import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import CloseIcon from '@mui/icons-material/CloseRounded';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import api from '../../services/api';

export default function ForgotPasswordModal({ open, onClose }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleClose = () => {
        setEmail('');
        setLoading(false);
        setSent(false);
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        setError('');
        try {
            await api.post('/api/access/askchangepassword/', { email: email.trim() });
            setSent(true);
        } catch {
            setSent(true); // Always show success to avoid email enumeration
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #701280 0%, #9B27AF 100%)',
                px: 3, py: 2.5,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
                        {t('forgotPassword.title')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
                        {t('forgotPassword.subtitle')}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ p: 3 }}>
                {sent ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 52, color: '#701280', mb: 1.5 }} />
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1c1c28', mb: 1 }}>
                            {t('forgotPassword.sentTitle')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B', lineHeight: 1.6 }}>
                            {t('forgotPassword.sentBody')}
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleClose}
                            sx={{
                                mt: 3,
                                background: 'linear-gradient(135deg, #701280, #9C3AAE)',
                                borderRadius: '11px',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                py: 1.3,
                                boxShadow: 'none',
                                '&:hover': { background: 'linear-gradient(135deg, #5A0F68, #7A2890)' },
                            }}
                        >
                            {t('forgotPassword.close')}
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#5B5B6B', mb: 2.5, lineHeight: 1.6 }}>
                            {t('forgotPassword.description')}
                        </Typography>
                        <TextField
                            fullWidth
                            type="email"
                            label={t('forgotPassword.emailLabel')}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={loading}
                            autoFocus
                            InputProps={{
                                startAdornment: <EmailIcon sx={{ color: '#9B9BA8', mr: 1, fontSize: '1.1rem' }} />,
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '11px',
                                    '&.Mui-focused fieldset': { borderColor: '#701280' },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#701280' },
                            }}
                        />
                        {error && (
                            <Typography sx={{ color: '#EF4444', fontSize: '0.82rem', mb: 1.5 }}>{error}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !email.trim()}
                            sx={{
                                background: 'linear-gradient(135deg, #701280, #9C3AAE)',
                                borderRadius: '11px',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                py: 1.3,
                                boxShadow: 'none',
                                '&:hover': { background: 'linear-gradient(135deg, #5A0F68, #7A2890)', boxShadow: '0 4px 16px rgba(112,18,128,0.3)' },
                                '&:disabled': { background: 'rgba(0,0,0,0.09)' },
                            }}
                        >
                            {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('forgotPassword.send')}
                        </Button>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}
